import {
	AlertOctagon,
	ArrowDownUp,
	Ban,
	Check,
	CheckCircle2,
	Eye,
	EyeOff,
	GitBranch,
	GitPullRequest,
	GitPullRequestClosed,
	Link2,
	RotateCcw,
	Search,
	Tag,
	User,
	X,
	XCircle,
} from "lucide-react";
import React, { useState, useEffect, useMemo, useRef } from "react";
import { ITEMS_PER_PAGE } from "../../constants";
import {
	escapeRegExp,
	getMRAuthorName,
	getMRAuthorUsername,
	getMRProjectPath,
	summarizeExclusionRule,
} from "../../exclusionRules";
import { fuzzyMatchFields, normalizeSearchQuery } from "../../fuzzySearch";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { useSectionOrder } from "../../hooks/useSectionOrder";
import { getWatchKey } from "../../hooks/useWatchedMRs";
import {
	DEFAULT_REPO_PATHS,
	buildCheckoutCommand,
	copyCheckoutCommand,
	copyMRLinks,
	formatProjectName,
} from "../../utils";
import { CollapsibleSection } from "../CollapsibleSection";
import { MRTagDialog, TagPill, mrMatchesActiveTagFilters } from "../MRTags";
import { MergeRequest } from "../MergeRequest";
import { PaginationButtons } from "../PaginationButtons";
import { RecentPushes } from "../RecentPushes";
import { SectionDragHandle } from "../SectionDragHandle";
import { Select } from "../Select";
import { Tooltip } from "../Tooltip";

const getMRKey = getWatchKey;

const DEFAULT_SECTION_CONTROLS = {
	author: "",
	repo: "",
	order: "newest",
	query: "",
};

// Roll-up state used by the "Your merge requests" section: differentiates
// open, failing (open + CI failing), merged, and closed.
export const getMyMRState = (mr) => {
	if (mr?.state === "merged") return "merged";
	if (mr?.state === "closed") return "closed";
	const pipeline = mr?.headPipeline;
	const failing =
		!!pipeline &&
		(pipeline.status === "failed" || (pipeline.failedJobs || 0) > 0);
	if (failing) return "failing";
	return "open";
};

const MY_MR_STATE_LABEL = {
	open: "Open",
	failing: "Failing",
	merged: "Merged",
	closed: "Closed",
};

const MY_MR_STATE_DOT_CLASS = {
	open: "bg-emerald-500",
	failing: "bg-rose-500",
	merged: "bg-violet-500",
	closed: "bg-slate-400",
};

const MyMRStatePill = ({ state }) => (
	<span
		className="inline-flex shrink-0 items-center gap-1 rounded-full border border-[var(--color-border)] bg-[var(--color-bg-primary)] px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-[var(--color-text-secondary)]"
		title={`State: ${MY_MR_STATE_LABEL[state]}`}
	>
		<span
			className={`h-1.5 w-1.5 rounded-full ${MY_MR_STATE_DOT_CLASS[state]}`}
			aria-hidden="true"
		/>
		{MY_MR_STATE_LABEL[state]}
	</span>
);

const MyMRStateToggles = ({ counts, toggles, onToggle }) => {
	// open/failing always on and read-only; merged/closed toggle inclusion.
	const items = [
		{ id: "open", locked: true, active: true },
		{ id: "failing", locked: true, active: true },
		{ id: "merged", locked: false, active: !!toggles.merged },
		{ id: "closed", locked: false, active: !!toggles.closed },
	];
	return (
		<div className="mb-3 flex flex-wrap items-center gap-2">
			{items.map(({ id, locked, active }) => {
				const label = MY_MR_STATE_LABEL[id];
				const count = counts[id] ?? 0;
				const base =
					"inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors";
				const style = active
					? "border-[var(--color-accent)] bg-[var(--color-accent-bg)] text-[var(--color-text-primary)]"
					: "border-[var(--color-border)] bg-[var(--color-bg-primary)] text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)]";
				const cursor = locked ? "cursor-default" : "cursor-pointer";
				return (
					<button
						key={id}
						type="button"
						onClick={() => (locked ? null : onToggle(id))}
						aria-pressed={active}
						aria-label={
							locked
								? `${label} (always shown)`
								: `${active ? "Hide" : "Show"} ${label.toLowerCase()} merge requests`
						}
						className={`${base} ${style} ${cursor}`}
					>
						<span
							className={`h-1.5 w-1.5 rounded-full ${MY_MR_STATE_DOT_CLASS[id]}`}
							aria-hidden="true"
						/>
						<span>{label}</span>
						<span className="text-[var(--color-text-tertiary)]">{count}</span>
					</button>
				);
			})}
		</div>
	);
};

const SORT_OPTIONS = [
	{ value: "newest", label: "Newest first" },
	{ value: "oldest", label: "Oldest first" },
];

const MAIN_SECTION = Object.freeze({
	PENDING_REVIEW: "pending-review",
	WATCHING: "watching",
	APPROVED: "approved-unmerged",
	MY_OPEN: "my-open",
	RECENTLY_CLOSED: "recently-closed",
	IGNORED: "ignored",
});

const MAIN_SECTION_LABELS = {
	[MAIN_SECTION.PENDING_REVIEW]: "Merge requests to review",
	[MAIN_SECTION.WATCHING]: "Watching",
	[MAIN_SECTION.APPROVED]: "Already approved",
	[MAIN_SECTION.MY_OPEN]: "Your merge requests",
	[MAIN_SECTION.RECENTLY_CLOSED]: "Recently Closed MRs",
	[MAIN_SECTION.IGNORED]: "Ignored",
};

// A stable per-author identifier, preferring username and falling back to the
// display name so authors without a username still get an option.
const authorKey = (mr) => getMRAuthorUsername(mr) || getMRAuthorName(mr);

const getMRSearchFields = (mr) => [
	mr.title,
	getMRAuthorName(mr),
	getMRAuthorUsername(mr),
	getMRProjectPath(mr),
	mr.iid && `!${mr.iid}`,
	mr.iid && `#${mr.iid}`,
	mr.sourceBranch,
];

const buildAuthorOptions = (mrs) => {
	const byKey = new Map();
	for (const mr of mrs) {
		const key = authorKey(mr);
		if (!key || byKey.has(key)) continue;
		byKey.set(key, getMRAuthorName(mr) || key);
	}
	return [
		{ value: "", label: "All authors" },
		...[...byKey.entries()]
			.sort((a, b) => a[1].localeCompare(b[1]))
			.map(([value, label]) => ({ value, label })),
	];
};

const buildRepoOptions = (mrs) => {
	const byPath = new Map();
	for (const mr of mrs) {
		const path = getMRProjectPath(mr);
		if (!path || byPath.has(path)) continue;
		byPath.set(path, formatProjectName(path));
	}
	return [
		{ value: "", label: "All repos" },
		...[...byPath.entries()]
			.sort((a, b) => a[1].localeCompare(b[1]))
			.map(([value, label]) => ({ value, label })),
	];
};

// Apply a section's author/repo filters and date sort. `dateField` is the
// primary date to sort on (e.g. "closedAt" for closed MRs) with fallbacks so
// every MR has a comparable value.
const applySectionControls = (mrs, controls, dateField = "updatedAt") => {
	const { author, repo, order, query } = {
		...DEFAULT_SECTION_CONTROLS,
		...controls,
	};
	const sectionQuery = normalizeSearchQuery(query);
	const matchesQuery = (mr) => {
		if (!sectionQuery) return true;

		return fuzzyMatchFields(getMRSearchFields(mr), sectionQuery);
	};

	let out = mrs;
	if (author) out = out.filter((mr) => authorKey(mr) === author);
	if (repo) out = out.filter((mr) => getMRProjectPath(mr) === repo);
	if (sectionQuery) out = out.filter(matchesQuery);
	return [...out].sort((a, b) => {
		const da = new Date(a[dateField] || a.updatedAt || a.createdAt || 0);
		const db = new Date(b[dateField] || b.updatedAt || b.createdAt || 0);
		return order === "oldest" ? da - db : db - da;
	});
};

const MRSearchInput = ({ value, onChange }) => (
	<div className="relative min-w-0 max-w-[10.5rem] flex-1 basis-[10.5rem]">
		<Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--color-text-tertiary)]" />
		<input
			type="text"
			value={value}
			onChange={(event) => onChange(event.target.value)}
			placeholder="Search MRs"
			aria-label="Search merge requests in this section"
			className="h-8 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-primary)] pl-8 pr-8 text-xs font-medium text-[var(--color-text-primary)] placeholder:text-[var(--color-text-tertiary)] focus:border-[var(--color-border-hover)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
		/>
		{value && (
			<button
				type="button"
				onClick={() => onChange("")}
				className="search-clear-button no-hover-lift absolute inset-y-0 right-1.5 my-auto inline-flex h-5 w-5 items-center justify-center rounded-md text-[var(--color-text-tertiary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
				aria-label="Clear section merge request search"
			>
				<X className="h-3.5 w-3.5" />
			</button>
		)}
	</div>
);

// Section-level search, filters, and sort rendered when a section is expanded.
const SectionFilterControls = ({
	authorOptions,
	repoOptions,
	value,
	onChange,
}) => (
	<div className="flex min-w-0 flex-1 flex-nowrap items-center gap-2">
		<MRSearchInput
			value={value.query}
			onChange={(query) => onChange({ query })}
		/>
		<Select
			className="min-w-0 max-w-[9.75rem] flex-1 basis-[9.75rem]"
			icon={User}
			ariaLabel="Filter by author"
			placeholder="All authors"
			value={value.author}
			options={authorOptions}
			onChange={(author) => onChange({ author })}
		/>
		<Select
			className="min-w-0 max-w-[9.75rem] flex-1 basis-[9.75rem]"
			icon={GitBranch}
			ariaLabel="Filter by repository"
			placeholder="All repos"
			value={value.repo}
			options={repoOptions}
			onChange={(repo) => onChange({ repo })}
		/>
		<Select
			className="min-w-0 max-w-[9.75rem] flex-1 basis-[9.75rem]"
			icon={ArrowDownUp}
			ariaLabel="Sort by date"
			value={value.order}
			options={SORT_OPTIONS}
			onChange={(order) => onChange({ order })}
		/>
	</div>
);

const clampContextMenuPosition = (x, y) => ({
	x: Math.max(8, Math.min(x, window.innerWidth - 216)),
	y: Math.max(8, Math.min(y, window.innerHeight - 64)),
});

const buildExcludeDraft = (mr) => {
	const projectPath = getMRProjectPath(mr);
	const authorName = getMRAuthorName(mr);
	const authorUsername = getMRAuthorUsername(mr);

	return {
		mr,
		includeTitle: false,
		includeRepo: !!projectPath,
		includeAuthor: false,
		titleRegex: escapeRegExp(mr.title || ""),
		projectPath,
		authorName,
		authorUsername,
		originalAuthorName: authorName,
		originalAuthorUsername: authorUsername,
	};
};

const buildExcludeRuleFromMR = (mr, options) => ({
	titleRegex: options.includeTitle ? escapeRegExp(mr.title || "") : "",
	projectPath: options.includeRepo ? getMRProjectPath(mr) : "",
	authorName: options.includeAuthor ? getMRAuthorName(mr) : "",
	authorUsername: options.includeAuthor ? getMRAuthorUsername(mr) : "",
	sourceMr: {
		id: mr.id,
		iid: mr.iid,
		title: mr.title,
		projectPath: getMRProjectPath(mr),
	},
});

const ExcludeLikeContextMenu = ({
	menu,
	menuRef,
	ignoredMRIds,
	watchedKeys = [],
	onWatchMRs,
	onUnwatchMRs,
	onIgnoreMRs,
	onUnignoreMRs,
	onOpenRuleForm,
	onCopyCheckout,
	onCopyMRLinks,
	onCloseMRs,
	allMRTags = [],
	getMRTags = () => [],
	onAddTagToMRs,
	onOpenTagDialog,
}) => {
	const [repoPaths] = useLocalStorage("repoPaths", DEFAULT_REPO_PATHS);
	if (!menu) return null;

	const targetMRs = menu.targetMRs?.length ? menu.targetMRs : [menu.mr];
	const targetMRsWithIds = targetMRs.filter((mr) => mr?.id);
	const ignoredTargets = targetMRsWithIds.filter((mr) =>
		ignoredMRIds.includes(mr.id),
	);
	const unignoredTargets = targetMRsWithIds.filter(
		(mr) => !ignoredMRIds.includes(mr.id),
	);
	const shouldUnignore =
		targetMRsWithIds.length > 0 &&
		ignoredTargets.length === targetMRsWithIds.length;
	const ignoreActionMRs = shouldUnignore ? ignoredTargets : unignoredTargets;
	const ignoreVerb = shouldUnignore ? "Unignore" : "Ignore";
	const isBulk = targetMRs.length > 1;
	const mrNoun = isBulk ? "MRs" : "MR";
	const ignoreLabel = `${ignoreVerb} ${mrNoun}${
		isBulk ? ` (${ignoreActionMRs.length})` : ""
	}`;
	const excludeLabel = `Exclude Like ${mrNoun}${
		isBulk ? ` (${targetMRs.length})` : ""
	}`;
	const copyLinksLabel = `Copy link${isBulk ? `s (${targetMRs.length})` : ""}`;
	const quickTagOptions = allMRTags
		.filter((tag) =>
			targetMRs.some(
				(mr) =>
					!getMRTags(mr).some(
						(mrTag) => mrTag.toLowerCase() === tag.toLowerCase(),
					),
			),
		)
		.slice(0, 5);

	const watchableTargets = targetMRs.filter(Boolean);
	const watchedTargets = watchableTargets.filter((mr) =>
		watchedKeys.includes(getWatchKey(mr)),
	);
	const shouldUnwatch =
		watchableTargets.length > 0 &&
		watchedTargets.length === watchableTargets.length;
	const watchActionMRs = shouldUnwatch
		? watchedTargets
		: watchableTargets.filter((mr) => !watchedKeys.includes(getWatchKey(mr)));
	const watchVerb = shouldUnwatch ? "Unwatch" : "Watch";
	const watchLabel = `${watchVerb} ${mrNoun}${
		isBulk ? ` (${watchActionMRs.length})` : ""
	}`;

	// Checkout command applies to the right-clicked MR only (checking out
	// multiple branches at once doesn't make sense). Hidden when the MR's repo
	// has no configured local path or no source branch.
	const checkoutCommand = buildCheckoutCommand(menu.mr, repoPaths);

	// Only the MR author can close their own opened MRs. Hide the option
	// entirely if none of the targets are closable by the current user.
	const closableTargets = targetMRs.filter(
		(mr) => mr?.author?.isMe && mr.state === "opened",
	);
	const canCloseAny = closableTargets.length > 0;
	const closeLabel = `Close ${mrNoun}${
		isBulk ? ` (${closableTargets.length})` : ""
	}`;

	return (
		<div
			ref={menuRef}
			role="menu"
			className="fixed z-50 min-w-48 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-primary)] py-1 shadow-xl"
			style={{ left: menu.x, top: menu.y }}
		>
			{checkoutCommand && (
				<button
					type="button"
					role="menuitem"
					onClick={() => onCopyCheckout?.(menu.mr)}
					title={checkoutCommand}
					className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[var(--color-text-primary)] hover:bg-[var(--color-bg-secondary)] focus:bg-[var(--color-bg-secondary)] focus:outline-none"
				>
					<GitBranch className="h-4 w-4 text-[var(--color-text-secondary)]" />
					Copy checkout command
				</button>
			)}
			<button
				type="button"
				role="menuitem"
				onClick={() => onCopyMRLinks?.(targetMRs)}
				className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[var(--color-text-primary)] hover:bg-[var(--color-bg-secondary)] focus:bg-[var(--color-bg-secondary)] focus:outline-none"
			>
				<Link2 className="h-4 w-4 text-[var(--color-text-secondary)]" />
				{copyLinksLabel}
			</button>
			<button
				type="button"
				role="menuitem"
				onClick={() => onOpenTagDialog?.(targetMRs)}
				className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[var(--color-text-primary)] hover:bg-[var(--color-bg-secondary)] focus:bg-[var(--color-bg-secondary)] focus:outline-none"
			>
				<Tag className="h-4 w-4 text-[var(--color-text-secondary)]" />
				Add tag{isBulk ? ` to ${targetMRs.length} MRs` : ""}
			</button>
			{quickTagOptions.length > 0 && (
				<div className="border-y border-[var(--color-border)] px-2 py-1">
					<div className="px-1 pb-1 text-[10px] font-semibold uppercase tracking-wide text-[var(--color-text-tertiary)]">
						Quick add tag
					</div>
					<div className="flex max-w-64 flex-wrap gap-1">
						{quickTagOptions.map((tag) => (
							<button
								key={tag.toLowerCase()}
								type="button"
								onClick={() => onAddTagToMRs?.(tag, targetMRs)}
								className="no-hover-lift"
								aria-label={`Add tag ${tag}`}
							>
								<TagPill tag={tag} className="max-w-[10rem]" />
							</button>
						))}
					</div>
				</div>
			)}
			<button
				type="button"
				role="menuitem"
				disabled={watchActionMRs.length === 0}
				onClick={() => {
					if (shouldUnwatch) {
						onUnwatchMRs?.(watchActionMRs);
					} else {
						onWatchMRs?.(watchActionMRs);
					}
				}}
				className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[var(--color-text-primary)] hover:bg-[var(--color-bg-secondary)] focus:bg-[var(--color-bg-secondary)] focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
			>
				{shouldUnwatch ? (
					<EyeOff className="h-4 w-4 text-[var(--color-text-secondary)]" />
				) : (
					<Eye className="h-4 w-4 text-[var(--color-text-secondary)]" />
				)}
				{watchLabel}
			</button>
			<button
				type="button"
				role="menuitem"
				disabled={ignoreActionMRs.length === 0}
				onClick={() => {
					if (shouldUnignore) {
						onUnignoreMRs?.(ignoreActionMRs);
					} else {
						onIgnoreMRs?.(ignoreActionMRs);
					}
				}}
				className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[var(--color-text-primary)] hover:bg-[var(--color-bg-secondary)] focus:bg-[var(--color-bg-secondary)] focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
			>
				{shouldUnignore ? (
					<RotateCcw className="h-4 w-4 text-[var(--color-text-secondary)]" />
				) : (
					<EyeOff className="h-4 w-4 text-[var(--color-text-secondary)]" />
				)}
				{ignoreLabel}
			</button>
			<button
				type="button"
				role="menuitem"
				onClick={onOpenRuleForm}
				className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[var(--color-text-primary)] hover:bg-[var(--color-bg-secondary)] focus:bg-[var(--color-bg-secondary)] focus:outline-none"
			>
				<Ban className="h-4 w-4 text-[var(--color-text-secondary)]" />
				{excludeLabel}
			</button>
			{canCloseAny && onCloseMRs && (
				<>
					<div className="my-1 h-px bg-[var(--color-border)]" />
					<button
						type="button"
						role="menuitem"
						onClick={() => onCloseMRs(closableTargets)}
						className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-600 hover:bg-[var(--color-bg-secondary)] focus:bg-[var(--color-bg-secondary)] focus:outline-none dark:text-red-400"
					>
						<XCircle className="h-4 w-4" />
						{closeLabel}
					</button>
				</>
			)}
		</div>
	);
};

const ExcludeLikeDialog = ({ draft, onChange, onClose, onSubmit }) => {
	const [error, setError] = useState("");

	if (!draft) return null;

	const updateDraft = (updates) => {
		setError("");
		onChange({ ...draft, ...updates });
	};

	const canSubmit =
		(draft.includeTitle && draft.titleRegex.trim()) ||
		(draft.includeRepo && draft.projectPath.trim()) ||
		(draft.includeAuthor && draft.authorName.trim());

	const handleSubmit = (event) => {
		event.preventDefault();

		if (!canSubmit) {
			setError("Select at least one exclude field.");
			return;
		}

		if (draft.includeTitle) {
			try {
				new RegExp(draft.titleRegex);
			} catch {
				setError("Title regex is invalid.");
				return;
			}
		}

		onSubmit({
			titleRegex: draft.includeTitle ? draft.titleRegex.trim() : "",
			projectPath: draft.includeRepo ? draft.projectPath.trim() : "",
			authorName: draft.includeAuthor ? draft.authorName.trim() : "",
			authorUsername: draft.includeAuthor ? draft.authorUsername : "",
			sourceMr: {
				id: draft.mr.id,
				iid: draft.mr.iid,
				title: draft.mr.title,
				projectPath: getMRProjectPath(draft.mr),
			},
		});
	};

	return (
		<dialog
			open
			className="fixed inset-0 z-50 m-0 flex h-screen max-h-none w-screen max-w-none items-center justify-center bg-black/60 px-4 text-left"
			aria-modal="true"
			aria-labelledby="exclude-like-title"
			onMouseDown={onClose}
		>
			<div
				className="w-full max-w-md rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-primary)] p-4 shadow-xl"
				onMouseDown={(event) => event.stopPropagation()}
			>
				<div className="mb-4 flex items-center justify-between gap-3">
					<h2
						id="exclude-like-title"
						className="text-base font-semibold text-[var(--color-text-primary)]"
					>
						Exclude Like
					</h2>
					<button
						type="button"
						onClick={onClose}
						className="rounded p-1 text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
						aria-label="Close exclude dialog"
					>
						<X className="h-4 w-4" />
					</button>
				</div>

				<form onSubmit={handleSubmit} className="space-y-3">
					<label className="flex items-start gap-3 rounded-md border border-[var(--color-border)] p-3">
						<input
							type="checkbox"
							checked={draft.includeTitle}
							onChange={(event) =>
								updateDraft({ includeTitle: event.target.checked })
							}
							className="mt-1 h-4 w-4 rounded border-[var(--color-border)] text-[var(--color-accent)]"
						/>
						<span className="min-w-0 flex-1">
							<span className="block text-sm font-medium text-[var(--color-text-primary)]">
								Title regex
							</span>
							<input
								type="text"
								value={draft.titleRegex}
								onChange={(event) =>
									updateDraft({ titleRegex: event.target.value })
								}
								disabled={!draft.includeTitle}
								className="mt-2 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-secondary)] px-3 py-2 text-sm text-[var(--color-text-primary)] disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
							/>
						</span>
					</label>

					<label className="flex items-start gap-3 rounded-md border border-[var(--color-border)] p-3">
						<input
							type="checkbox"
							checked={draft.includeRepo}
							onChange={(event) =>
								updateDraft({ includeRepo: event.target.checked })
							}
							className="mt-1 h-4 w-4 rounded border-[var(--color-border)] text-[var(--color-accent)]"
						/>
						<span className="min-w-0 flex-1">
							<span className="block text-sm font-medium text-[var(--color-text-primary)]">
								Repository
							</span>
							<input
								type="text"
								value={draft.projectPath}
								onChange={(event) =>
									updateDraft({ projectPath: event.target.value })
								}
								disabled={!draft.includeRepo}
								className="mt-2 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-secondary)] px-3 py-2 text-sm text-[var(--color-text-primary)] disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
							/>
						</span>
					</label>

					<label className="flex items-start gap-3 rounded-md border border-[var(--color-border)] p-3">
						<input
							type="checkbox"
							checked={draft.includeAuthor}
							onChange={(event) =>
								updateDraft({ includeAuthor: event.target.checked })
							}
							className="mt-1 h-4 w-4 rounded border-[var(--color-border)] text-[var(--color-accent)]"
						/>
						<span className="min-w-0 flex-1">
							<span className="block text-sm font-medium text-[var(--color-text-primary)]">
								Author
							</span>
							<input
								type="text"
								value={draft.authorName}
								onChange={(event) => {
									const authorName = event.target.value;
									updateDraft({
										authorName,
										authorUsername:
											authorName === draft.originalAuthorName
												? draft.originalAuthorUsername
												: "",
									});
								}}
								disabled={!draft.includeAuthor}
								className="mt-2 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-secondary)] px-3 py-2 text-sm text-[var(--color-text-primary)] disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
							/>
						</span>
					</label>

					{error && (
						<p className="text-sm text-[var(--color-error-text)]">{error}</p>
					)}

					<div className="flex justify-end gap-2 pt-2">
						<button
							type="button"
							onClick={onClose}
							className="inline-flex items-center gap-2 rounded-md border border-[var(--color-border)] px-3 py-2 text-sm text-[var(--color-text-primary)] hover:bg-[var(--color-bg-secondary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
						>
							<X className="h-4 w-4" />
							Cancel
						</button>
						<button
							type="submit"
							disabled={!canSubmit}
							className="inline-flex items-center gap-2 rounded-md bg-[var(--color-action)] px-3 py-2 text-sm font-medium text-[var(--color-action-text)] hover:bg-[var(--color-action-hover)] disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
						>
							<Check className="h-4 w-4" />
							Exclude
						</button>
					</div>
				</form>
			</div>
		</dialog>
	);
};

const BulkExcludeLikeDialog = ({ draft, onChange, onClose, onSubmit }) => {
	const [error, setError] = useState("");

	if (!draft) return null;

	const updateDraft = (updates) => {
		setError("");
		onChange({ ...draft, ...updates });
	};

	const canSubmit =
		draft.includeTitle || draft.includeRepo || draft.includeAuthor;

	const handleSubmit = (event) => {
		event.preventDefault();

		if (!canSubmit) {
			setError("Select at least one exclude field.");
			return;
		}

		onSubmit(draft);
	};

	return (
		<dialog
			open
			className="fixed inset-0 z-50 m-0 flex h-screen max-h-none w-screen max-w-none items-center justify-center bg-black/60 px-4 text-left"
			aria-modal="true"
			aria-labelledby="bulk-exclude-like-title"
			onMouseDown={onClose}
		>
			<div
				className="w-full max-w-md rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-primary)] p-4 shadow-xl"
				onMouseDown={(event) => event.stopPropagation()}
			>
				<div className="mb-4 flex items-center justify-between gap-3">
					<h2
						id="bulk-exclude-like-title"
						className="text-base font-semibold text-[var(--color-text-primary)]"
					>
						Exclude Like Selected
					</h2>
					<button
						type="button"
						onClick={onClose}
						className="rounded p-1 text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
						aria-label="Close bulk exclude dialog"
					>
						<X className="h-4 w-4" />
					</button>
				</div>

				<form onSubmit={handleSubmit} className="space-y-3">
					<p className="text-sm text-[var(--color-text-secondary)]">
						Create matching rules for {draft.mrs.length} selected MRs.
					</p>

					<label className="flex items-center gap-3 rounded-md border border-[var(--color-border)] p-3 text-sm font-medium text-[var(--color-text-primary)]">
						<input
							type="checkbox"
							checked={draft.includeTitle}
							onChange={(event) =>
								updateDraft({ includeTitle: event.target.checked })
							}
							className="h-4 w-4 rounded border-[var(--color-border)] text-[var(--color-accent)]"
						/>
						Title regex for each selected MR
					</label>

					<label className="flex items-center gap-3 rounded-md border border-[var(--color-border)] p-3 text-sm font-medium text-[var(--color-text-primary)]">
						<input
							type="checkbox"
							checked={draft.includeRepo}
							onChange={(event) =>
								updateDraft({ includeRepo: event.target.checked })
							}
							className="h-4 w-4 rounded border-[var(--color-border)] text-[var(--color-accent)]"
						/>
						Repository for each selected MR
					</label>

					<label className="flex items-center gap-3 rounded-md border border-[var(--color-border)] p-3 text-sm font-medium text-[var(--color-text-primary)]">
						<input
							type="checkbox"
							checked={draft.includeAuthor}
							onChange={(event) =>
								updateDraft({ includeAuthor: event.target.checked })
							}
							className="h-4 w-4 rounded border-[var(--color-border)] text-[var(--color-accent)]"
						/>
						Author for each selected MR
					</label>

					{error && (
						<p className="text-sm text-[var(--color-error-text)]">{error}</p>
					)}

					<div className="flex justify-end gap-2 pt-2">
						<button
							type="button"
							onClick={onClose}
							className="inline-flex items-center gap-2 rounded-md border border-[var(--color-border)] px-3 py-2 text-sm text-[var(--color-text-primary)] hover:bg-[var(--color-bg-secondary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
						>
							<X className="h-4 w-4" />
							Cancel
						</button>
						<button
							type="submit"
							disabled={!canSubmit}
							className="inline-flex items-center gap-2 rounded-md bg-[var(--color-action)] px-3 py-2 text-sm font-medium text-[var(--color-action-text)] hover:bg-[var(--color-action-hover)] disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
						>
							<Check className="h-4 w-4" />
							Apply Rules
						</button>
					</div>
				</form>
			</div>
		</dialog>
	);
};

const CloseMRDialog = ({ mrs, busy, error, onClose, onConfirm }) => {
	if (!mrs || mrs.length === 0) return null;

	const isBulk = mrs.length > 1;
	const title = isBulk
		? `Close ${mrs.length} merge requests?`
		: "Close merge request?";

	return (
		<dialog
			open
			className="fixed inset-0 z-50 m-0 flex h-screen max-h-none w-screen max-w-none items-center justify-center bg-black/60 px-4 text-left"
			aria-modal="true"
			aria-labelledby="close-mr-title"
			onMouseDown={busy ? undefined : onClose}
		>
			<div
				className="w-full max-w-md rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-primary)] p-4 shadow-xl"
				onMouseDown={(event) => event.stopPropagation()}
			>
				<div className="mb-3 flex items-center justify-between gap-3">
					<h2
						id="close-mr-title"
						className="text-base font-semibold text-[var(--color-text-primary)]"
					>
						{title}
					</h2>
					<button
						type="button"
						onClick={onClose}
						disabled={busy}
						className="rounded p-1 text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] disabled:opacity-50"
						aria-label="Close dialog"
					>
						<X className="h-4 w-4" />
					</button>
				</div>

				<p className="text-sm text-[var(--color-text-secondary)]">
					This will close{" "}
					{isBulk ? "these merge requests" : "this merge request"} on GitLab.
					You can reopen from GitLab if needed.
				</p>

				<ul className="mt-3 max-h-48 overflow-y-auto rounded-md border border-[var(--color-border)] bg-[var(--color-bg-secondary)] p-2 text-sm">
					{mrs.map((mr) => (
						<li
							key={getMRKey(mr)}
							className="truncate py-0.5 text-[var(--color-text-primary)]"
							title={mr.title}
						>
							<span className="text-[var(--color-text-tertiary)]">
								{formatProjectName(getMRProjectPath(mr))} !{mr.iid}
							</span>{" "}
							{mr.title}
						</li>
					))}
				</ul>

				{error && (
					<p className="mt-3 text-sm text-[var(--color-error-text)]">{error}</p>
				)}

				<div className="mt-4 flex justify-end gap-2">
					<button
						type="button"
						onClick={onClose}
						disabled={busy}
						className="inline-flex items-center gap-2 rounded-md border border-[var(--color-border)] px-3 py-2 text-sm text-[var(--color-text-primary)] hover:bg-[var(--color-bg-secondary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] disabled:opacity-50"
					>
						Cancel
					</button>
					<button
						type="button"
						onClick={onConfirm}
						disabled={busy}
						className="inline-flex items-center gap-2 rounded-md bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:cursor-not-allowed disabled:opacity-60"
					>
						<XCircle className="h-4 w-4" />
						{busy ? "Closing..." : isBulk ? `Close ${mrs.length}` : "Close MR"}
					</button>
				</div>
			</div>
		</dialog>
	);
};

const BulkSelectionToolbar = ({
	selectedCount,
	onCopyLinks,
	onIgnore,
	onOpenExcludeRules,
	onClear,
}) => {
	if (selectedCount === 0) return null;

	const iconButtonClass =
		"no-hover-lift inline-flex h-7 w-7 items-center justify-center rounded-md hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]";

	return (
		<div className="flex h-8 min-w-0 flex-nowrap items-center justify-end gap-1 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-secondary)] px-1">
			<span className="whitespace-nowrap px-2 text-xs font-medium text-[var(--color-text-secondary)]">
				{selectedCount} selected
			</span>
			<div className="flex flex-nowrap items-center gap-1">
				<Tooltip content="Copy selected MR links">
					<button
						type="button"
						onClick={onCopyLinks}
						className={`${iconButtonClass} text-[var(--color-text-secondary)]`}
						aria-label="Copy selected merge request links"
					>
						<Link2 className="h-3.5 w-3.5" />
					</button>
				</Tooltip>
				<Tooltip content="Ignore selected MRs">
					<button
						type="button"
						onClick={onIgnore}
						className={`${iconButtonClass} text-[var(--color-text-secondary)]`}
						aria-label="Ignore selected merge requests"
					>
						<EyeOff className="h-3.5 w-3.5" />
					</button>
				</Tooltip>
				<Tooltip content="Exclude selected MRs">
					<button
						type="button"
						onClick={onOpenExcludeRules}
						className={`${iconButtonClass} text-[var(--color-text-secondary)]`}
						aria-label="Exclude selected merge requests"
					>
						<Ban className="h-3.5 w-3.5" />
					</button>
				</Tooltip>
				<Tooltip content="Clear selection">
					<button
						type="button"
						onClick={onClear}
						className={`${iconButtonClass} text-[var(--color-text-tertiary)]`}
						aria-label="Clear selection"
					>
						<X className="h-3.5 w-3.5" />
					</button>
				</Tooltip>
			</div>
		</div>
	);
};

const ExclusionRuleChips = ({ rules, onRemoveRule }) => {
	if (!rules?.length) return null;

	return (
		<div className="mb-3 flex flex-wrap gap-2">
			{rules.map((rule) => (
				<span
					key={rule.id}
					className="inline-flex max-w-full items-center gap-2 rounded-full border border-[var(--color-border)] bg-[var(--color-bg-primary)] px-3 py-1 text-xs text-[var(--color-text-secondary)]"
					title={summarizeExclusionRule(rule)}
				>
					<span className="truncate">{summarizeExclusionRule(rule)}</span>
					<button
						type="button"
						onClick={() => onRemoveRule(rule.id)}
						className="rounded-full p-0.5 hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
						aria-label={`Remove ${summarizeExclusionRule(rule)}`}
					>
						<X className="h-3 w-3" />
					</button>
				</span>
			))}
		</div>
	);
};

export const StandardView = ({
	pendingReviewMRs,
	myOpenMRs,
	myMergedMRs = [],
	myClosedMRs = [],
	recentlyClosedMRs,
	loading,
	loadingClosed,
	error,
	pushesWithoutMR,
	sortOption,
	showPendingWithProjectRules,
	showPendingWithAllApprovals,
	greyOutBlockingComments,
	enableCommentsExpansion = false,
	excludedMRs = [],
	watchedMRs = [],
	watchedMRState,
	excludeLikeRules = [],
	onAddExcludeLikeRule,
	onRemoveExcludeLikeRule,
	onClearExcludeLikeRules,
	ignoredMRIds = [],
	onIgnoreMR,
	onUnignoreMR,
	onCloseMR,
	refetch,
	onOpenDiff,
	watchingOnly = false,
	showWatchingSection = false,
	searchQuery = "",
	activeTagFilter = [],
	getMRTags = () => [],
	allMRTags = [],
	onAddMRTag,
	onRemoveMRTag,
}) => {
	const [pendingReviewPage, setPendingReviewPage] = useState(1);
	const [watchingPage, setWatchingPage] = useState(1);
	const [approvedPage, setApprovedPage] = useState(1);
	const [myOpenPage, setMyOpenPage] = useState(1);
	const [recentlyClosedPage, setRecentlyClosedPage] = useState(1);
	const [excludedPage, setExcludedPage] = useState(1);
	const [contextMenu, setContextMenu] = useState(null);
	const [draggingSectionId, setDraggingSectionId] = useState(null);
	const [sectionDropTarget, setSectionDropTarget] = useState(null);
	const [repoPaths] = useLocalStorage("repoPaths", DEFAULT_REPO_PATHS);
	const [excludeDraft, setExcludeDraft] = useState(null);
	const [bulkExcludeDraft, setBulkExcludeDraft] = useState(null);
	const [tagDraftMRs, setTagDraftMRs] = useState(null);
	const [closeMRDraft, setCloseMRDraft] = useState(null);
	const [closingMRs, setClosingMRs] = useState(false);
	const [closeMRError, setCloseMRError] = useState(null);
	const [selectedMRKeys, setSelectedMRKeys] = useState([]);
	const [selectionAnchorKey, setSelectionAnchorKey] = useState(null);
	const [sectionControls, setSectionControls] = useLocalStorage(
		"dashboard-section-controls",
		{},
	);
	const [myMRStateToggles, setMyMRStateToggles] = useLocalStorage(
		"dashboard-my-mr-state-toggles",
		{ merged: false, closed: false },
	);
	const toggleMyMRState = (state) =>
		setMyMRStateToggles((prev) => ({ ...prev, [state]: !prev[state] }));
	const [expandedSectionId, setExpandedSectionId] = useLocalStorage(
		"dashboard-main-expanded-section",
		MAIN_SECTION.PENDING_REVIEW,
	);
	const contextMenuRef = useRef(null);
	const excludedCount = excludedMRs.length;

	const getControls = (key) => ({
		...DEFAULT_SECTION_CONTROLS,
		...sectionControls[key],
	});
	const updateControls = (key, patch) => {
		setSectionControls((prev) => ({
			...prev,
			[key]: { ...DEFAULT_SECTION_CONTROLS, ...prev[key], ...patch },
		}));
	};
	const {
		watchedIds = [],
		snapshots = {},
		isWatched = () => false,
		watchMR = () => {},
		unwatchMR = () => {},
		markSeen = () => {},
		getActivity = () => null,
	} = watchedMRState || {};

	useEffect(() => {
		void sortOption;
		void sectionControls;
		void activeTagFilter;
		setPendingReviewPage(1);
		setWatchingPage(1);
		setApprovedPage(1);
		setMyOpenPage(1);
		setRecentlyClosedPage(1);
		setExcludedPage(1);
	}, [sortOption, sectionControls, activeTagFilter]);

	useEffect(() => {
		void excludedCount;
		setExcludedPage(1);
	}, [excludedCount]);

	useEffect(() => {
		if (!contextMenu) return undefined;

		const handleMouseDown = (event) => {
			if (contextMenuRef.current?.contains(event.target)) {
				return;
			}

			setContextMenu(null);
		};
		const handleKeyDown = (event) => {
			if (event.key === "Escape") setContextMenu(null);
		};
		const handleScroll = () => setContextMenu(null);

		document.addEventListener("mousedown", handleMouseDown);
		document.addEventListener("keydown", handleKeyDown);
		document.addEventListener("scroll", handleScroll, true);

		return () => {
			document.removeEventListener("mousedown", handleMouseDown);
			document.removeEventListener("keydown", handleKeyDown);
			document.removeEventListener("scroll", handleScroll, true);
		};
	}, [contextMenu]);

	useEffect(() => {
		if (!excludeDraft) return undefined;

		const handleKeyDown = (event) => {
			if (event.key === "Escape") setExcludeDraft(null);
		};

		document.addEventListener("keydown", handleKeyDown);
		return () => document.removeEventListener("keydown", handleKeyDown);
	}, [excludeDraft]);

	useEffect(() => {
		if (!bulkExcludeDraft) return undefined;

		const handleKeyDown = (event) => {
			if (event.key === "Escape") setBulkExcludeDraft(null);
		};

		document.addEventListener("keydown", handleKeyDown);
		return () => document.removeEventListener("keydown", handleKeyDown);
	}, [bulkExcludeDraft]);

	const handleMRContextMenu = (event, mr) => {
		event.preventDefault();
		event.stopPropagation();
		setExcludeDraft(null);
		setBulkExcludeDraft(null);
		const mrKey = getMRKey(mr);
		const selectedTargets = selectedMRKeys
			.map((key) => selectableMRByKey.get(key))
			.filter(Boolean);
		const targetMRs =
			selectedTargets.length > 1 && selectedMRKeys.includes(mrKey)
				? selectedTargets
				: [mr];
		setContextMenu({
			mr,
			targetMRs,
			...clampContextMenuPosition(event.clientX, event.clientY),
		});
	};

	const openExcludeRuleForm = () => {
		if (!contextMenu?.mr) return;

		const targetMRs = contextMenu.targetMRs?.length
			? contextMenu.targetMRs
			: [contextMenu.mr];

		if (targetMRs.length > 1) {
			setBulkExcludeDraft({
				mrs: targetMRs,
				includeTitle: false,
				includeRepo: true,
				includeAuthor: false,
			});
		} else {
			setExcludeDraft(buildExcludeDraft(contextMenu.mr));
		}
		setContextMenu(null);
	};

	const addExcludeRule = (rule) => {
		onAddExcludeLikeRule?.(rule, excludeDraft?.mr ? [excludeDraft.mr] : []);
		setExcludeDraft(null);
	};

	const watchMRsFromMenu = (mrs) => {
		for (const mr of mrs) watchMR(mr);
		setContextMenu(null);
		clearSelection();
	};

	const unwatchMRsFromMenu = (mrs) => {
		for (const mr of mrs) unwatchMR(mr);
		setContextMenu(null);
		clearSelection();
	};

	const ignoreMRsFromMenu = (mrs) => {
		for (const mr of mrs) {
			if (mr.id) onIgnoreMR?.(mr);
		}
		setContextMenu(null);
		clearSelection();
	};

	const unignoreMRsFromMenu = (mrs) => {
		for (const mr of mrs) {
			if (mr.id) onUnignoreMR?.(mr);
		}
		setContextMenu(null);
		clearSelection();
	};

	const copyCheckoutFromMenu = (mr) => {
		copyCheckoutCommand(mr, repoPaths);
		setContextMenu(null);
	};

	const copyMRLinksFromMenu = (mrs) => {
		copyMRLinks(mrs);
		setContextMenu(null);
	};

	const addTagToMRs = (tag, mrs) => {
		for (const mr of mrs) {
			onAddMRTag?.(mr, tag);
		}
		setContextMenu(null);
	};

	const openTagDialogFromMenu = (mrs) => {
		setTagDraftMRs(mrs);
		setContextMenu(null);
	};

	const openCloseMRDialogFromMenu = (mrs) => {
		setCloseMRError(null);
		setCloseMRDraft(mrs);
		setContextMenu(null);
	};

	const confirmCloseMRs = async () => {
		if (!closeMRDraft?.length || !onCloseMR) return;
		setClosingMRs(true);
		setCloseMRError(null);
		try {
			const results = await Promise.allSettled(
				closeMRDraft.map((mr) => onCloseMR(mr)),
			);
			const failed = results.filter((r) => r.status === "rejected");
			if (failed.length > 0) {
				setCloseMRError(
					`Failed to close ${failed.length} of ${closeMRDraft.length} MR${
						closeMRDraft.length > 1 ? "s" : ""
					}: ${failed[0].reason?.message || "Unknown error"}`,
				);
				return;
			}
			setCloseMRDraft(null);
			clearSelection();
			await refetch?.();
		} finally {
			setClosingMRs(false);
		}
	};

	const copySelectedMRLinks = (mrs = selectedMRs) => {
		copyMRLinks(mrs);
	};

	const reviewScores = {
		UNREVIEWED: 1,
		UNAPPROVED: 1,
		REVIEW_STARTED: 0,
		REVIEWED: -1,
		REQUESTED_CHANGES: -1,
		APPROVED: -1,
	};

	const sortedPendingReviewMRs = [...pendingReviewMRs].sort((a, b) => {
		if (a.draft !== b.draft) return a.draft ? 1 : -1;

		const aHasFailedMergeTrain =
			a.headPipeline?.mergeRequestEventType === "MERGE_TRAIN" &&
			a.headPipeline?.failedJobs > 0;
		const bHasFailedMergeTrain =
			b.headPipeline?.mergeRequestEventType === "MERGE_TRAIN" &&
			b.headPipeline?.failedJobs > 0;
		if (aHasFailedMergeTrain !== bHasFailedMergeTrain)
			return aHasFailedMergeTrain ? -1 : 1;

		const aReview = reviewScores[a.myReview];
		const bReview = reviewScores[b.myReview];
		if (aReview !== bReview) {
			return bReview - aReview;
		}

		if (greyOutBlockingComments) {
			const aHasBlockingHumanDiscussion = a.discussions > 0 && a.hasHumanReview;
			const bHasBlockingHumanDiscussion = b.discussions > 0 && b.hasHumanReview;
			if (aHasBlockingHumanDiscussion !== bHasBlockingHumanDiscussion) {
				return aHasBlockingHumanDiscussion ? 1 : -1;
			}
		}

		if ((a.author?.bot ?? false) !== (b.author?.bot ?? false)) {
			return (a.author?.bot ?? false) ? 1 : -1;
		}

		if (sortOption === "createdAt") {
			return new Date(b.createdAt) - new Date(a.createdAt);
		}
		return new Date(b.updatedAt) - new Date(a.updatedAt);
	});

	// Combine authored MRs across states based on which toggles are on. Open
	// and failing are always shown; merged/closed opt-in.
	const combinedMyMRs = [
		...myOpenMRs,
		...(myMRStateToggles.merged ? myMergedMRs : []),
		...(myMRStateToggles.closed ? myClosedMRs : []),
	];
	const myMRStateCounts = {
		open: myOpenMRs.filter((mr) => getMyMRState(mr) === "open").length,
		failing: myOpenMRs.filter((mr) => getMyMRState(mr) === "failing").length,
		merged: myMergedMRs.length,
		closed: myClosedMRs.length,
	};
	const sortedMyOpenMRs = [...combinedMyMRs].sort((a, b) => {
		if (sortOption === "createdAt") {
			return new Date(b.createdAt) - new Date(a.createdAt);
		}
		return new Date(b.updatedAt) - new Date(a.updatedAt);
	});

	const sortedRecentlyClosedMRs = [...recentlyClosedMRs].sort((a, b) => {
		return (
			new Date(b.closedAt || b.updatedAt) - new Date(a.closedAt || a.updatedAt)
		);
	});

	const sortedExcludedMRs = [...excludedMRs].sort((a, b) => {
		return (
			new Date(b.updatedAt || b.createdAt) -
			new Date(a.updatedAt || a.createdAt)
		);
	});

	const filterPendingMRs = (mrs) => {
		return mrs
			.filter(
				(mr) =>
					showPendingWithProjectRules || !mr.additionalRules?.blocking?.length,
			)
			.filter((mr) => showPendingWithAllApprovals || mr.approvalsLeft > 0);
	};
	const isApprovedButUnmerged = (mr) =>
		mr.state === "opened" && mr.myReview === "APPROVED";
	const matchesActiveTag = (mr) =>
		mrMatchesActiveTagFilters(getMRTags(mr), activeTagFilter);
	const globalFilterMRs = (mrs) =>
		mrs.filter(
			(mr) =>
				matchesActiveTag(mr) &&
				(!searchQuery || fuzzyMatchFields(getMRSearchFields(mr), searchQuery)),
		);

	const filteredApprovedMRs = globalFilterMRs(
		sortedPendingReviewMRs.filter(isApprovedButUnmerged),
	);
	const filteredPendingReviewMRs = globalFilterMRs(
		filterPendingMRs(
			sortedPendingReviewMRs.filter((mr) => !isApprovedButUnmerged(mr)),
		),
	);
	const filteredMyOpenMRs = globalFilterMRs(sortedMyOpenMRs);
	const filteredRecentlyClosedMRs = globalFilterMRs(sortedRecentlyClosedMRs);
	const filteredExcludedMRs = globalFilterMRs(sortedExcludedMRs);

	// Resolve every watched MR against the freshest data we have, falling back
	// to the snapshot copy so a watched MR still renders if it has dropped out
	// of all the fetched lists.
	const knownMRByKey = new Map();
	for (const mr of [
		...pendingReviewMRs,
		...myOpenMRs,
		...recentlyClosedMRs,
		...watchedMRs,
		...excludedMRs,
	]) {
		knownMRByKey.set(getMRKey(mr), mr);
	}
	const watchingMRs = globalFilterMRs(
		watchedIds
			.map((key) => knownMRByKey.get(key) || snapshots[key]?.mr)
			.filter(Boolean)
			.sort((a, b) => {
				const aActive = getActivity(a) ? 1 : 0;
				const bActive = getActivity(b) ? 1 : 0;
				if (aActive !== bActive) return bActive - aActive;
				return new Date(b.updatedAt) - new Date(a.updatedAt);
			}),
	);

	// Per-section author/repo filter + date sort. Option lists are derived from
	// each section's pre-control list so the choices stay stable while filtering.
	const pendingControls = getControls("dashboard-pending-review");
	const watchingControls = getControls("dashboard-watching");
	const approvedControls = getControls("dashboard-approved-unmerged");
	const myOpenControls = getControls("dashboard-my-open");
	const recentlyClosedControls = getControls("dashboard-recently-closed");
	const excludedControls = getControls("dashboard-ignored");

	const displayPendingReviewMRs = applySectionControls(
		filteredPendingReviewMRs,
		pendingControls,
	);
	const displayWatchingMRs = applySectionControls(
		watchingMRs,
		watchingControls,
	);
	const displayApprovedMRs = applySectionControls(
		filteredApprovedMRs,
		approvedControls,
	);
	const displayMyOpenMRs = applySectionControls(
		filteredMyOpenMRs,
		myOpenControls,
	);
	const displayRecentlyClosedMRs = applySectionControls(
		filteredRecentlyClosedMRs,
		recentlyClosedControls,
		"closedAt",
	);
	const displayExcludedMRs = applySectionControls(
		filteredExcludedMRs,
		excludedControls,
	);

	const visiblePendingReviewMRs = displayPendingReviewMRs.slice(
		(pendingReviewPage - 1) * ITEMS_PER_PAGE,
		pendingReviewPage * ITEMS_PER_PAGE,
	);
	const visibleWatchingMRs = displayWatchingMRs.slice(
		(watchingPage - 1) * ITEMS_PER_PAGE,
		watchingPage * ITEMS_PER_PAGE,
	);
	const visibleApprovedMRs = displayApprovedMRs.slice(
		(approvedPage - 1) * ITEMS_PER_PAGE,
		approvedPage * ITEMS_PER_PAGE,
	);
	const visibleMyOpenMRs = displayMyOpenMRs.slice(
		(myOpenPage - 1) * ITEMS_PER_PAGE,
		myOpenPage * ITEMS_PER_PAGE,
	);
	const visibleRecentlyClosedMRs = displayRecentlyClosedMRs.slice(
		(recentlyClosedPage - 1) * ITEMS_PER_PAGE,
		recentlyClosedPage * ITEMS_PER_PAGE,
	);
	const visibleExcludedMRs = displayExcludedMRs.slice(
		(excludedPage - 1) * ITEMS_PER_PAGE,
		excludedPage * ITEMS_PER_PAGE,
	);
	const allSelectableMRs = watchingOnly
		? [...displayWatchingMRs]
		: [
				...(showWatchingSection ? displayWatchingMRs : []),
				...displayPendingReviewMRs,
				...displayApprovedMRs,
				...displayMyOpenMRs,
				...displayRecentlyClosedMRs,
				...displayExcludedMRs,
			];
	const visibleSelectableMRs = watchingOnly
		? [...visibleWatchingMRs]
		: [
				...(showWatchingSection ? visibleWatchingMRs : []),
				...visiblePendingReviewMRs,
				...visibleApprovedMRs,
				...visibleMyOpenMRs,
				...visibleRecentlyClosedMRs,
				...visibleExcludedMRs,
			];
	const selectableMRByKey = new Map(
		allSelectableMRs.map((mr) => [getMRKey(mr), mr]),
	);
	const selectedMRs = selectedMRKeys
		.map((key) => selectableMRByKey.get(key))
		.filter(Boolean);
	const selectedMRKeySet = new Set(selectedMRKeys);

	const clearMRSelection = (mrs) => {
		const keysToClear = new Set(mrs.map(getMRKey));

		setSelectedMRKeys((prev) => prev.filter((key) => !keysToClear.has(key)));
		setSelectionAnchorKey((anchorKey) =>
			keysToClear.has(anchorKey) ? null : anchorKey,
		);
	};

	const clearSelection = () => {
		setSelectedMRKeys([]);
		setSelectionAnchorKey(null);
	};

	const handleMRSelect = (event, mr) => {
		const key = getMRKey(mr);
		const isToggleModifier = event.metaKey || event.ctrlKey;
		if (!event.shiftKey && !isToggleModifier && onOpenDiff) {
			onOpenDiff(mr);
			return;
		}
		const visibleKeys = visibleSelectableMRs.map(getMRKey);
		const currentIndex = visibleKeys.indexOf(key);
		const anchorIndex = selectionAnchorKey
			? visibleKeys.indexOf(selectionAnchorKey)
			: -1;

		setSelectedMRKeys((prev) => {
			const next = new Set(prev);

			if (next.has(key)) {
				next.delete(key);
			} else if (
				event.shiftKey &&
				anchorIndex !== -1 &&
				currentIndex !== -1 &&
				selectionAnchorKey !== key
			) {
				const start = Math.min(anchorIndex, currentIndex);
				const end = Math.max(anchorIndex, currentIndex);
				for (const rangeKey of visibleKeys.slice(start, end + 1)) {
					next.add(rangeKey);
				}
			} else {
				next.add(key);
			}

			return [...next];
		});
		setSelectionAnchorKey(key);
	};

	const ignoreSelectedMRs = (mrs = selectedMRs) => {
		for (const mr of mrs) {
			if (mr.id) onIgnoreMR?.(mr);
		}
		clearMRSelection(mrs);
	};

	const openBulkExcludeRules = (mrs = selectedMRs) => {
		if (mrs.length === 0) return;

		setBulkExcludeDraft({
			mrs,
			includeTitle: false,
			includeRepo: true,
			includeAuthor: false,
		});
	};

	const applyBulkExcludeRules = (draft) => {
		for (const mr of draft.mrs) {
			const rule = buildExcludeRuleFromMR(mr, draft);
			if (rule.titleRegex || rule.projectPath || rule.authorName) {
				onAddExcludeLikeRule?.(rule, [mr]);
			}
		}
		setBulkExcludeDraft(null);
		clearMRSelection(draft.mrs);
	};

	const selectedMRsFor = (mrs) =>
		mrs.filter((mr) => selectedMRKeySet.has(getMRKey(mr)));

	const renderSelectionToolbar = (mrs) => {
		const sectionSelectedMRs = selectedMRsFor(mrs);
		if (sectionSelectedMRs.length === 0) return null;

		return (
			<BulkSelectionToolbar
				selectedCount={sectionSelectedMRs.length}
				onCopyLinks={() => copySelectedMRLinks(sectionSelectedMRs)}
				onIgnore={() => ignoreSelectedMRs(sectionSelectedMRs)}
				onOpenExcludeRules={() => openBulkExcludeRules(sectionSelectedMRs)}
				onClear={() => clearMRSelection(sectionSelectedMRs)}
			/>
		);
	};

	const combineHeaderActions = (...actions) => {
		const visibleActions = actions.filter(Boolean);
		if (visibleActions.length === 0) return null;

		return (
			<div className="flex w-full min-w-0 flex-nowrap items-center gap-2">
				{visibleActions.map((action, index) => (
					<React.Fragment key={action.key || `header-action-${index}`}>
						{action}
					</React.Fragment>
				))}
			</div>
		);
	};

	const visibleMainSectionIds = useMemo(
		() => [
			MAIN_SECTION.PENDING_REVIEW,
			...(showWatchingSection && watchingMRs.length > 0
				? [MAIN_SECTION.WATCHING]
				: []),
			...(filteredApprovedMRs.length > 0 ? [MAIN_SECTION.APPROVED] : []),
			MAIN_SECTION.MY_OPEN,
			MAIN_SECTION.RECENTLY_CLOSED,
			...(excludeLikeRules.length > 0 || filteredExcludedMRs.length > 0
				? [MAIN_SECTION.IGNORED]
				: []),
		],
		[
			showWatchingSection,
			watchingMRs.length,
			filteredApprovedMRs.length,
			excludeLikeRules.length,
			filteredExcludedMRs.length,
		],
	);
	const { order: mainSectionOrder, moveSection: moveMainSection } =
		useSectionOrder("dashboard-main-section-order", visibleMainSectionIds);

	useEffect(() => {
		if (expandedSectionId === null) return;
		if (visibleMainSectionIds.includes(expandedSectionId)) return;
		setExpandedSectionId(visibleMainSectionIds[0] ?? null);
	}, [expandedSectionId, setExpandedSectionId, visibleMainSectionIds]);

	const clearSectionDrag = () => {
		setDraggingSectionId(null);
		setSectionDropTarget(null);
	};

	const handleSectionDragOver = (event, targetId) => {
		if (!draggingSectionId || draggingSectionId === targetId) return;

		event.preventDefault();
		event.dataTransfer.dropEffect = "move";
		const rect = event.currentTarget.getBoundingClientRect();
		const edge =
			event.clientY < rect.top + rect.height / 2 ? "before" : "after";
		setSectionDropTarget((prev) =>
			prev?.id === targetId && prev.edge === edge
				? prev
				: { id: targetId, edge },
		);
	};

	const handleSectionDrop = (event, targetId) => {
		if (!draggingSectionId) return;

		event.preventDefault();
		const edge =
			sectionDropTarget?.id === targetId ? sectionDropTarget.edge : "before";
		moveMainSection(draggingSectionId, targetId, edge);
		clearSectionDrag();
	};

	const sectionReorderProps = (sectionId) => ({
		dragHandle: (
			<SectionDragHandle
				id={sectionId}
				label={MAIN_SECTION_LABELS[sectionId]}
				interactive={false}
			/>
		),
		dragId: sectionId,
		dragLabel: MAIN_SECTION_LABELS[sectionId],
		dropEdge:
			sectionDropTarget?.id === sectionId ? sectionDropTarget.edge : "before",
		isDragging: draggingSectionId === sectionId,
		isDropTarget:
			sectionDropTarget?.id === sectionId && draggingSectionId !== sectionId,
		forceCollapsed: Boolean(draggingSectionId),
		onSectionDragStart: setDraggingSectionId,
		onSectionDragEnd: clearSectionDrag,
		onDragOver: (event) => handleSectionDragOver(event, sectionId),
		onDrop: (event) => handleSectionDrop(event, sectionId),
		style: { order: mainSectionOrder.indexOf(sectionId) },
	});

	const sectionAccordionProps = (sectionId) => ({
		collapsed: expandedSectionId !== sectionId,
		onCollapsedChange: (nextCollapsed) =>
			setExpandedSectionId(nextCollapsed ? null : sectionId),
	});

	if (loading && !watchingOnly) {
		return (
			<div className="flex flex-col">
				{[1, 2, 3].map((section) => (
					<div
						key={section}
						className="bg-[var(--color-bg-primary)] rounded shadow p-4"
					>
						<div className="flex items-center gap-2 mb-4">
							<div className="w-8 h-8 rounded bg-[var(--color-bg-secondary)] animate-pulse" />
							<div className="w-48 h-5 bg-[var(--color-bg-secondary)] rounded animate-pulse" />
						</div>
						{[1, 2].map((item) => (
							<div
								key={item}
								className="mb-4 py-4 border-b last:border-0 border-[var(--color-border)]"
							>
								<div className="flex justify-between items-start mb-2">
									<div className="w-3/4 h-5 bg-[var(--color-bg-secondary)] rounded animate-pulse" />
									<div className="w-16 h-5 bg-[var(--color-bg-secondary)] rounded animate-pulse" />
								</div>
								<div className="flex gap-2 mt-2">
									<div className="w-20 h-4 bg-[var(--color-bg-secondary)] rounded animate-pulse" />
									<div className="w-24 h-4 bg-[var(--color-bg-secondary)] rounded animate-pulse" />
								</div>
							</div>
						))}
					</div>
				))}
			</div>
		);
	}

	const renderWatchingContent = () =>
		displayWatchingMRs.length > 0 ? (
			<>
				{visibleWatchingMRs.map((mr) => (
					<MergeRequest
						key={getMRKey(mr)}
						mr={mr}
						greyOutBlockingComments={greyOutBlockingComments}
						isMyOpenMR={mr.author?.isMe}
						enableCommentsExpansion={enableCommentsExpansion}
						onContextMenu={handleMRContextMenu}
						onSelect={handleMRSelect}
						isSelected={selectedMRKeySet.has(getMRKey(mr))}
						isWatched={isWatched(mr)}
						activity={getActivity(mr)}
						onActivitySeen={markSeen}
						onOpenDiff={onOpenDiff}
						searchQuery={searchQuery}
						tags={getMRTags(mr)}
						allTags={allMRTags}
						onAddTag={onAddMRTag}
						onRemoveTag={onRemoveMRTag}
					/>
				))}
				<PaginationButtons
					total={displayWatchingMRs.length}
					currentPage={watchingPage}
					onPageChange={setWatchingPage}
					itemsPerPage={ITEMS_PER_PAGE}
				/>
			</>
		) : (
			<div className="bg-[var(--color-info-bg)] text-[var(--color-info-text)] px-4 py-3 rounded">
				No watched merge requests
			</div>
		);

	const renderWatchingSection = (sectionProps = {}) => (
		<CollapsibleSection
			variant="repo"
			storageKey={
				watchingOnly ? "dashboard-watching-tab" : "dashboard-watching"
			}
			count={displayWatchingMRs.length}
			title="Watching"
			icon={Eye}
			iconColor="text-[var(--color-accent)]"
			selectionBar={renderSelectionToolbar(displayWatchingMRs)}
			headerActions={combineHeaderActions(
				watchingMRs.length > 0 && (
					<SectionFilterControls
						authorOptions={buildAuthorOptions(watchingMRs)}
						repoOptions={buildRepoOptions(watchingMRs)}
						value={watchingControls}
						onChange={(patch) => updateControls("dashboard-watching", patch)}
					/>
				),
			)}
			{...sectionProps}
		>
			{renderWatchingContent()}
		</CollapsibleSection>
	);

	return (
		<>
			<ExcludeLikeContextMenu
				menu={contextMenu}
				menuRef={contextMenuRef}
				ignoredMRIds={ignoredMRIds}
				watchedKeys={watchedIds}
				onWatchMRs={watchMRsFromMenu}
				onUnwatchMRs={unwatchMRsFromMenu}
				onIgnoreMRs={ignoreMRsFromMenu}
				onUnignoreMRs={unignoreMRsFromMenu}
				onOpenRuleForm={openExcludeRuleForm}
				onCopyCheckout={copyCheckoutFromMenu}
				onCopyMRLinks={copyMRLinksFromMenu}
				onCloseMRs={onCloseMR ? openCloseMRDialogFromMenu : undefined}
				allMRTags={allMRTags}
				getMRTags={getMRTags}
				onAddTagToMRs={addTagToMRs}
				onOpenTagDialog={openTagDialogFromMenu}
			/>

			<CloseMRDialog
				mrs={closeMRDraft}
				busy={closingMRs}
				error={closeMRError}
				onClose={() => {
					if (closingMRs) return;
					setCloseMRDraft(null);
					setCloseMRError(null);
				}}
				onConfirm={confirmCloseMRs}
			/>

			<ExcludeLikeDialog
				draft={excludeDraft}
				onChange={setExcludeDraft}
				onClose={() => setExcludeDraft(null)}
				onSubmit={addExcludeRule}
			/>

			<BulkExcludeLikeDialog
				draft={bulkExcludeDraft}
				onChange={setBulkExcludeDraft}
				onClose={() => setBulkExcludeDraft(null)}
				onSubmit={applyBulkExcludeRules}
			/>

			<MRTagDialog
				open={!!tagDraftMRs}
				mrs={tagDraftMRs || []}
				allTags={allMRTags}
				onClose={() => setTagDraftMRs(null)}
				onSubmit={addTagToMRs}
			/>

			{!watchingOnly && <RecentPushes pushes={pushesWithoutMR} />}

			{error && (
				<div className="bg-[var(--color-error-bg)] border border-[var(--color-error-border)] text-[var(--color-error-text)] px-4 py-3 rounded-lg mb-6 shadow-sm">
					<p className="font-medium">Error loading data</p>
					<p className="text-sm">{error}</p>
				</div>
			)}

			{watchingOnly ? (
				<div>{renderWatchingSection({ collapsed: false })}</div>
			) : (
				<div className="flex flex-col">
					<CollapsibleSection
						variant="repo"
						storageKey="dashboard-pending-review"
						{...sectionReorderProps(MAIN_SECTION.PENDING_REVIEW)}
						{...sectionAccordionProps(MAIN_SECTION.PENDING_REVIEW)}
						count={displayPendingReviewMRs.length}
						title="Merge requests to review"
						icon={AlertOctagon}
						iconColor="text-orange-500"
						selectionBar={renderSelectionToolbar(displayPendingReviewMRs)}
						headerActions={combineHeaderActions(
							filteredPendingReviewMRs.length > 0 && (
								<SectionFilterControls
									authorOptions={buildAuthorOptions(filteredPendingReviewMRs)}
									repoOptions={buildRepoOptions(filteredPendingReviewMRs)}
									value={pendingControls}
									onChange={(patch) =>
										updateControls("dashboard-pending-review", patch)
									}
								/>
							),
						)}
					>
						{displayPendingReviewMRs.length > 0 ? (
							<>
								{visiblePendingReviewMRs.map((mr) => (
									<MergeRequest
										key={getMRKey(mr)}
										mr={mr}
										greyOutBlockingComments={greyOutBlockingComments}
										enableCommentsExpansion={enableCommentsExpansion}
										onContextMenu={handleMRContextMenu}
										onSelect={handleMRSelect}
										isSelected={selectedMRKeySet.has(getMRKey(mr))}
										isWatched={isWatched(mr)}
										activity={getActivity(mr)}
										onActivitySeen={markSeen}
										onOpenDiff={onOpenDiff}
										searchQuery={searchQuery}
										tags={getMRTags(mr)}
										allTags={allMRTags}
										onAddTag={onAddMRTag}
										onRemoveTag={onRemoveMRTag}
									/>
								))}
								<PaginationButtons
									total={displayPendingReviewMRs.length}
									currentPage={pendingReviewPage}
									onPageChange={setPendingReviewPage}
									itemsPerPage={ITEMS_PER_PAGE}
								/>
							</>
						) : (
							<div className="bg-[var(--color-info-bg)] text-[var(--color-info-text)] px-4 py-3 rounded">
								No merge requests pending your review
							</div>
						)}
					</CollapsibleSection>

					{showWatchingSection &&
						watchingMRs.length > 0 &&
						renderWatchingSection({
							...sectionReorderProps(MAIN_SECTION.WATCHING),
							...sectionAccordionProps(MAIN_SECTION.WATCHING),
						})}

					{filteredApprovedMRs.length > 0 && (
						<CollapsibleSection
							variant="repo"
							storageKey="dashboard-approved-unmerged"
							{...sectionReorderProps(MAIN_SECTION.APPROVED)}
							{...sectionAccordionProps(MAIN_SECTION.APPROVED)}
							count={displayApprovedMRs.length}
							title="Already approved"
							icon={CheckCircle2}
							iconColor="text-emerald-500"
							defaultCollapsed={true}
							selectionBar={renderSelectionToolbar(displayApprovedMRs)}
							headerActions={combineHeaderActions(
								<SectionFilterControls
									authorOptions={buildAuthorOptions(filteredApprovedMRs)}
									repoOptions={buildRepoOptions(filteredApprovedMRs)}
									value={approvedControls}
									onChange={(patch) =>
										updateControls("dashboard-approved-unmerged", patch)
									}
								/>,
							)}
						>
							{visibleApprovedMRs.map((mr) => (
								<MergeRequest
									key={getMRKey(mr)}
									mr={mr}
									greyOutBlockingComments={greyOutBlockingComments}
									enableCommentsExpansion={enableCommentsExpansion}
									onContextMenu={handleMRContextMenu}
									onSelect={handleMRSelect}
									isSelected={selectedMRKeySet.has(getMRKey(mr))}
									isWatched={isWatched(mr)}
									activity={getActivity(mr)}
									onActivitySeen={markSeen}
									onOpenDiff={onOpenDiff}
									searchQuery={searchQuery}
									tags={getMRTags(mr)}
									allTags={allMRTags}
									onAddTag={onAddMRTag}
									onRemoveTag={onRemoveMRTag}
								/>
							))}
							<PaginationButtons
								total={displayApprovedMRs.length}
								currentPage={approvedPage}
								onPageChange={setApprovedPage}
								itemsPerPage={ITEMS_PER_PAGE}
							/>
						</CollapsibleSection>
					)}

					<CollapsibleSection
						variant="repo"
						storageKey="dashboard-my-open"
						{...sectionReorderProps(MAIN_SECTION.MY_OPEN)}
						{...sectionAccordionProps(MAIN_SECTION.MY_OPEN)}
						count={displayMyOpenMRs.length}
						title="Your merge requests"
						icon={GitPullRequest}
						iconColor="text-[var(--color-accent)]"
						selectionBar={renderSelectionToolbar(displayMyOpenMRs)}
						headerActions={combineHeaderActions(
							filteredMyOpenMRs.length > 0 && (
								<SectionFilterControls
									authorOptions={buildAuthorOptions(filteredMyOpenMRs)}
									repoOptions={buildRepoOptions(filteredMyOpenMRs)}
									value={myOpenControls}
									onChange={(patch) =>
										updateControls("dashboard-my-open", patch)
									}
								/>
							),
						)}
					>
						<MyMRStateToggles
							counts={myMRStateCounts}
							toggles={myMRStateToggles}
							onToggle={toggleMyMRState}
						/>
						{displayMyOpenMRs.length > 0 ? (
							<>
								{visibleMyOpenMRs.map((mr) => (
									<MergeRequest
										key={getMRKey(mr)}
										mr={mr}
										greyOutBlockingComments={greyOutBlockingComments}
										isMyOpenMR={true}
										enableCommentsExpansion={enableCommentsExpansion}
										statePill={<MyMRStatePill state={getMyMRState(mr)} />}
										onContextMenu={handleMRContextMenu}
										onSelect={handleMRSelect}
										isSelected={selectedMRKeySet.has(getMRKey(mr))}
										isWatched={isWatched(mr)}
										activity={getActivity(mr)}
										onActivitySeen={markSeen}
										onOpenDiff={onOpenDiff}
										searchQuery={searchQuery}
										tags={getMRTags(mr)}
										allTags={allMRTags}
										onAddTag={onAddMRTag}
										onRemoveTag={onRemoveMRTag}
									/>
								))}
								<PaginationButtons
									total={displayMyOpenMRs.length}
									currentPage={myOpenPage}
									onPageChange={setMyOpenPage}
									itemsPerPage={ITEMS_PER_PAGE}
								/>
							</>
						) : (
							<div className="bg-[var(--color-info-bg)] text-[var(--color-info-text)] px-4 py-3 rounded">
								You haven't created any open merge requests
							</div>
						)}
					</CollapsibleSection>

					<CollapsibleSection
						variant="repo"
						storageKey="dashboard-recently-closed"
						{...sectionReorderProps(MAIN_SECTION.RECENTLY_CLOSED)}
						{...sectionAccordionProps(MAIN_SECTION.RECENTLY_CLOSED)}
						count={loadingClosed ? "..." : displayRecentlyClosedMRs.length}
						title="Recently Closed MRs"
						icon={GitPullRequestClosed}
						iconColor="text-[var(--color-text-tertiary)]"
						selectionBar={renderSelectionToolbar(displayRecentlyClosedMRs)}
						headerActions={combineHeaderActions(
							filteredRecentlyClosedMRs.length > 0 && (
								<SectionFilterControls
									authorOptions={buildAuthorOptions(filteredRecentlyClosedMRs)}
									repoOptions={buildRepoOptions(filteredRecentlyClosedMRs)}
									value={recentlyClosedControls}
									onChange={(patch) =>
										updateControls("dashboard-recently-closed", patch)
									}
								/>
							),
						)}
					>
						{loadingClosed ? (
							<div className="h-48 bg-[var(--color-bg-secondary)] rounded animate-pulse" />
						) : displayRecentlyClosedMRs.length > 0 ? (
							<>
								{visibleRecentlyClosedMRs.map((mr) => (
									<MergeRequest
										key={getMRKey(mr)}
										mr={mr}
										enableCommentsExpansion={enableCommentsExpansion}
										onContextMenu={handleMRContextMenu}
										onSelect={handleMRSelect}
										isSelected={selectedMRKeySet.has(getMRKey(mr))}
										isWatched={isWatched(mr)}
										activity={getActivity(mr)}
										onActivitySeen={markSeen}
										onOpenDiff={onOpenDiff}
										searchQuery={searchQuery}
										tags={getMRTags(mr)}
										allTags={allMRTags}
										onAddTag={onAddMRTag}
										onRemoveTag={onRemoveMRTag}
									/>
								))}
								<PaginationButtons
									total={displayRecentlyClosedMRs.length}
									currentPage={recentlyClosedPage}
									onPageChange={setRecentlyClosedPage}
									itemsPerPage={ITEMS_PER_PAGE}
								/>
							</>
						) : (
							<div className="bg-[var(--color-info-bg)] text-[var(--color-info-text)] px-4 py-3 rounded">
								No recently closed merge requests
							</div>
						)}
					</CollapsibleSection>

					{(excludeLikeRules.length > 0 || filteredExcludedMRs.length > 0) && (
						<CollapsibleSection
							variant="repo"
							storageKey="dashboard-ignored"
							{...sectionReorderProps(MAIN_SECTION.IGNORED)}
							{...sectionAccordionProps(MAIN_SECTION.IGNORED)}
							count={displayExcludedMRs.length}
							title="Ignored"
							icon={EyeOff}
							iconColor="text-[var(--color-text-tertiary)]"
							defaultCollapsed={true}
							selectionBar={renderSelectionToolbar(displayExcludedMRs)}
							headerActions={combineHeaderActions(
								filteredExcludedMRs.length > 0 && (
									<SectionFilterControls
										authorOptions={buildAuthorOptions(filteredExcludedMRs)}
										repoOptions={buildRepoOptions(filteredExcludedMRs)}
										value={excludedControls}
										onChange={(patch) =>
											updateControls("dashboard-ignored", patch)
										}
									/>
								),
								excludeLikeRules.length > 0 && (
									<button
										type="button"
										onClick={onClearExcludeLikeRules}
										className="no-hover-lift h-8 whitespace-nowrap rounded-md border border-[var(--color-border)] px-3 text-xs font-medium text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
									>
										Clear rules
									</button>
								),
							)}
						>
							<ExclusionRuleChips
								rules={excludeLikeRules}
								onRemoveRule={onRemoveExcludeLikeRule}
							/>

							{displayExcludedMRs.length > 0 ? (
								<div className="pb-10">
									{visibleExcludedMRs.map((mr, index) => (
										<MergeRequest
											key={getMRKey(mr)}
											mr={mr}
											greyOutBlockingComments={greyOutBlockingComments}
											isMyOpenMR={mr.author?.isMe}
											enableCommentsExpansion={enableCommentsExpansion}
											onContextMenu={handleMRContextMenu}
											onSelect={handleMRSelect}
											isSelected={selectedMRKeySet.has(getMRKey(mr))}
											isWatched={isWatched(mr)}
											activity={getActivity(mr)}
											onActivitySeen={markSeen}
											onOpenDiff={onOpenDiff}
											searchQuery={searchQuery}
											tags={getMRTags(mr)}
											allTags={allMRTags}
											onAddTag={onAddMRTag}
											onRemoveTag={onRemoveMRTag}
											className={
												index === visibleExcludedMRs.length - 1 ? "mb-0" : ""
											}
										/>
									))}
									<PaginationButtons
										total={displayExcludedMRs.length}
										currentPage={excludedPage}
										onPageChange={setExcludedPage}
										itemsPerPage={ITEMS_PER_PAGE}
									/>
								</div>
							) : (
								<div className="bg-[var(--color-info-bg)] text-[var(--color-info-text)] px-4 py-3 rounded">
									No merge requests match active exclude rules
								</div>
							)}
						</CollapsibleSection>
					)}
				</div>
			)}
		</>
	);
};
